import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail, downloadExport } from "../lib/api";
import { PageHeader, EmptyState } from "../components/Shared";
import { fmtDateTime } from "../lib/format";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { TableSkeleton } from "../components/Skeletons";
import { Trash2, Search, Loader2, AlertTriangle, Download, FileText } from "lucide-react";

const ACTION_COLORS = {
  create: "bg-emerald-100 text-emerald-800",
  update: "bg-blue-100 text-blue-800",
  delete: "bg-red-100 text-red-800",
  approve: "bg-emerald-100 text-emerald-800",
  reject: "bg-red-100 text-red-800",
  login: "bg-slate-100 text-slate-700",
  register: "bg-sky-100 text-sky-800",
  set_role: "bg-purple-100 text-purple-800",
  set_status: "bg-amber-100 text-amber-800",
};

export default function AuditTrail() {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [count, setCount] = useState(null);
  const [checking, setChecking] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const qc = useQueryClient();
  const { data: rows = [], isLoading: rowsLoading } = useQuery({ queryKey: ["audit-trail"], queryFn: () => api.get("/audit-trail").then((r) => r.data) });
  const load = () => qc.invalidateQueries();

  useEffect(() => {
    if (!open || !from || !to) { setCount(null); return; }
    setChecking(true);
    api.get("/audit-trail/count", { params: { from, to } })
      .then(({ data }) => setCount(data.count))
      .catch(() => setCount(null))
      .finally(() => setChecking(false));
  }, [from, to, open]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r) =>
      [r.user_name, r.user_role, r.action, r.entity_type, r.entity_id, r.details ? JSON.stringify(r.details) : ""]
        .join(" ").toLowerCase().includes(term));
  }, [rows, search]);

  const rangeValid = from && to && from <= to;

  const doDelete = async () => {
    setDeleting(true);
    try {
      const { data } = await api.delete("/audit-trail", { params: { from, to } });
      toast.success(`Deleted ${data.deleted} audit ${data.deleted === 1 ? "entry" : "entries"}.`);
      setOpen(false); setFrom(""); setTo(""); setCount(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
    setDeleting(false);
  };

  return (
    <div>
      <PageHeader title="Audit Trail" subtitle="Track important changes to financial and business data" />

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[12rem] max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="audit-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by user, action, entity or details…" className="pl-10 h-9" />
        </div>
        <Button variant="outline" data-testid="audit-export-csv" onClick={() => downloadExport("/export/audit-trail?format=csv", "stems_audit_trail.csv")} className="gap-2">
          <Download className="h-4 w-4" /> CSV
        </Button>
        <Button variant="outline" data-testid="audit-export-pdf" onClick={() => downloadExport("/export/audit-trail?format=pdf", "stems_audit_trail.pdf")} className="gap-2">
          <FileText className="h-4 w-4" /> PDF
        </Button>
        <Button variant="outline" data-testid="audit-delete-open" onClick={() => setOpen(true)}
          className="gap-2 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700">
          <Trash2 className="h-4 w-4" /> Delete by period
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {rowsLoading ? <TableSkeleton cols={6} /> : filtered.length === 0 ? (
          <EmptyState text={search ? "No entries match your search." : "No activity recorded yet."} />
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>When</TableHead><TableHead>User</TableHead><TableHead>Action</TableHead>
              <TableHead>Entity</TableHead><TableHead>Reference</TableHead><TableHead>Details</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map((r) => (
                <TableRow key={r.id} data-testid={`audit-row-${r.id}`}>
                  <TableCell className="text-slate-500 whitespace-nowrap align-top">{fmtDateTime(r.created_at)}</TableCell>
                  <TableCell className="align-top"><span className="font-medium text-slate-800">{r.user_name}</span><span className="block text-xs text-slate-400">{r.user_role}</span></TableCell>
                  <TableCell className="align-top"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${ACTION_COLORS[r.action] || "bg-slate-100 text-slate-700"}`}>{r.action}</span></TableCell>
                  <TableCell className="text-slate-600 capitalize align-top">{(r.entity_type || "").replace(/_/g, " ")}</TableCell>
                  <TableCell className="text-slate-400 text-xs font-mono-num align-top whitespace-nowrap" title={r.entity_id || ""}>{r.entity_id ? `${r.entity_id}`.slice(0, 10) : "—"}</TableCell>
                  <TableCell className="text-slate-600 text-xs align-top max-w-sm" data-testid={`audit-details-${r.id}`}>
                    {r.details && Object.keys(r.details).length ? (
                      <div className="space-y-0.5">
                        {Object.entries(r.details).map(([k, v]) => (
                          <div key={k}><span className="text-slate-400">{k.replace(/_/g, " ")}:</span> <span className="font-medium break-words">{v !== null && typeof v === "object" ? JSON.stringify(v) : String(v)}</span></div>
                        ))}
                      </div>
                    ) : <span className="text-slate-400">—</span>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={open} onOpenChange={(v) => { if (!v && !deleting) { setOpen(false); } }}>
        <DialogContent data-testid="audit-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700"><Trash2 className="h-5 w-5" /> Delete audit logs</DialogTitle>
            <DialogDescription>Permanently remove all audit entries recorded between the two dates (inclusive). This cannot be undone.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="audit-from">From</Label>
              <Input id="audit-from" data-testid="audit-from" type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="audit-to">To</Label>
              <Input id="audit-to" data-testid="audit-to" type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
            </div>
          </div>

          {from && to && from > to && (
            <p className="text-sm text-red-600">The “From” date must be on or before the “To” date.</p>
          )}

          {rangeValid && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 flex items-center gap-2" data-testid="audit-delete-preview">
              {checking ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Counting entries…</>
              ) : (
                <><AlertTriangle className="h-4 w-4" />
                  <span><b data-testid="audit-delete-count">{count ?? 0}</b> {count === 1 ? "entry" : "entries"} will be permanently deleted.</span></>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={deleting}>Cancel</Button>
            <Button data-testid="audit-delete-confirm" onClick={doDelete}
              disabled={!rangeValid || checking || deleting || !count}
              className="bg-red-600 hover:bg-red-700 gap-2">
              {deleting && <Loader2 className="h-4 w-4 animate-spin" />}
              Delete {rangeValid && count ? `${count} ${count === 1 ? "entry" : "entries"}` : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
